import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, X, Plus, Check, GripVertical } from "lucide-react";
import { api, adminHeaders } from "../lib/api";
import { compressImageFile } from "../lib/imageCompress";
import { coverSrc } from "../lib/images";
import CoverImage from "./CoverImage";

const MAX_PICKS = 20;
const PAGE = 60;
const cellInput = "w-full bg-transparent border border-line rounded-sm px-3 py-2 text-vellum text-sm focus:border-crimson focus:outline-none placeholder:text-vellum-muted";

function buildPayload(item, changes) {
  const payload = { ...item, ...changes };
  delete payload.id;
  delete payload._id;
  return payload;
}

function PickRow({ item, index, total, onMove, onRemove, onSave, busy, drag }) {
  const [note, setNote] = useState(item.note || "");
  const [cover, setCover] = useState(item.cover_url || "");
  const [saving, setSaving] = useState(false);
  const [dropping, setDropping] = useState(false);
  const dirty = note !== (item.note || "") || cover !== (item.cover_url || "");

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Please drop an image file"); return; }
    try { setCover(await compressImageFile(file, { max: 500 })); }
    catch { toast.error("Couldn't read that image"); }
  };

  const save = async () => {
    setSaving(true);
    try { await onSave(item, { note, cover_url: cover }); }
    finally { setSaving(false); }
  };

  return (
    <div
      data-pick-row=""
      onPointerMove={() => drag.onOver(index)}
      className={`grid grid-cols-[auto_auto_56px_1.4fr_2fr_auto] gap-4 items-start px-4 py-4 transition-colors ${drag.sourceIndex === index ? "opacity-50" : ""} ${drag.targetIndex === index && drag.sourceIndex !== index ? "bg-crimson/10 outline outline-1 outline-crimson" : ""}`}
      data-testid={`pick-row-${item.id}`}
    >
      <button
        type="button"
        onPointerDown={(e) => { e.preventDefault(); drag.onStart(index); }}
        aria-label={`Drag to reorder ${item.title}`}
        title="Drag to reorder"
        data-testid={`pick-handle-${item.id}`}
        style={{ touchAction: "none" }}
        className="mt-1 cursor-grab active:cursor-grabbing text-vellum-muted hover:text-brass transition-colors"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="flex flex-col gap-1 pt-1">
        <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0 || busy} data-testid={`pick-up-${item.id}`} className="text-vellum-muted hover:text-crimson disabled:opacity-20 disabled:cursor-not-allowed transition-colors"><ArrowUp className="w-4 h-4" /></button>
        <button type="button" onClick={() => onMove(index, 1)} disabled={index === total - 1 || busy} data-testid={`pick-down-${item.id}`} className="text-vellum-muted hover:text-crimson disabled:opacity-20 disabled:cursor-not-allowed transition-colors"><ArrowDown className="w-4 h-4" /></button>
      </div>
      <div
        className={`relative w-14 h-20 shrink-0 bg-ink-700 overflow-hidden border cursor-pointer ${dropping ? "border-crimson ring-2 ring-crimson/40" : "border-line"}`}
        onDragOver={(e) => { e.preventDefault(); setDropping(true); }}
        onDragLeave={() => setDropping(false)}
        onDrop={(e) => { e.preventDefault(); setDropping(false); handleFile(e.dataTransfer.files?.[0]); }}
        title="Drop an image to replace the cover"
        data-testid={`pick-dropzone-${item.id}`}
      >
        {cover ? <img src={coverSrc(cover, "thumb")} loading="lazy" decoding="async" alt="" className="w-full h-full object-cover" /> : <span className="flex items-center justify-center w-full h-full text-[9px] text-vellum-muted">no cover</span>}
      </div>
      <div className="min-w-0">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-crimson">{item.media_type}</p>
        <p className="font-serif text-lg text-vellum truncate">{item.title}</p>
        <p className="font-mono text-xs text-vellum-muted truncate">{item.creator}{item.year ? ` · ${item.year}` : ""}</p>
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className={cellInput} placeholder="Spotlight note…" data-testid={`pick-note-${item.id}`} />
      <div className="flex flex-col items-end gap-2">
        <button type="button" onClick={() => onRemove(item)} disabled={busy} data-testid={`pick-remove-${item.id}`} className="text-vellum-muted hover:text-crimson transition-colors disabled:opacity-40"><X className="w-4 h-4" /></button>
        {dirty && (
          <button type="button" onClick={save} disabled={saving} data-testid={`pick-save-${item.id}`} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 bg-vellum text-ink hover:bg-crimson hover:text-onaccent transition-colors disabled:opacity-40 whitespace-nowrap">
            <Check className="w-3 h-3" /> {saving ? "…" : "Save"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminPicksManager({ library, onDone }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [sourceIndex, setSourceIndex] = useState(null);
  const [targetIndex, setTargetIndex] = useState(null);
  const from = useRef(null);
  const to = useRef(null);
  const dragging = useRef(false);
  const listRef = useRef(null);
  const rowsRef = useRef([]);

  const picks = [...library].filter((i) => i.featured).sort((a, b) => (a.sort || 0) - (b.sort || 0));
  useEffect(() => { if (!dragging.current) setRows(picks); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [library]);

  const putItem = (item, changes) => api.put(`/library/${item.id}`, buildPayload(item, changes), { headers: adminHeaders() });

  const persistOrder = async (ordered) => {
    setBusy(true);
    try {
      await Promise.all(ordered.map((item, i) => putItem(item, { sort: i })));
      toast.success("Order saved");
      onDone?.();
    } catch { toast.error("Reorder failed"); }
    finally { setBusy(false); }
  };

  const move = (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= rows.length) return;
    const reordered = [...rows];
    [reordered[index], reordered[j]] = [reordered[j], reordered[index]];
    setRows(reordered);
    persistOrder(reordered);
  };

  // Pointer-driven reordering: the row order is committed once on drop (mouse,
  // pen or touch), which keeps it reliable and testable.
  rowsRef.current = rows;
  useEffect(() => {
    const onUp = () => {
      if (!dragging.current) return;
      const src = from.current;
      const dst = to.current;
      dragging.current = false;
      from.current = null;
      to.current = null;
      setSourceIndex(null);
      setTargetIndex(null);
      if (src === null || dst === null || src === dst) return;
      const next = [...rowsRef.current];
      const [moved] = next.splice(src, 1);
      next.splice(dst, 0, moved);
      setRows(next);
      persistOrder(next);
    };
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const drag = {
    sourceIndex,
    targetIndex,
    onStart: (index) => {
      dragging.current = true;
      from.current = index;
      to.current = index;
      setSourceIndex(index);
      setTargetIndex(index);
    },
    onOver: (index) => {
      if (!dragging.current || to.current === index) return;
      to.current = index;
      setTargetIndex(index);
    },
  };

  const remove = async (item) => {
    setBusy(true);
    try { await putItem(item, { featured: false }); toast.success(`Removed "${item.title}" from the spotlight`); onDone?.(); }
    catch { toast.error("Couldn't remove"); }
    finally { setBusy(false); }
  };

  const add = async (item) => {
    if (rows.length >= MAX_PICKS) { toast.error(`Only ${MAX_PICKS} spotlight slots available`); return; }
    setBusy(true);
    try {
      await putItem(item, { featured: true, sort: rows.length });
      toast.success(`Added "${item.title}" to the spotlight`);
      onDone?.();
    } catch { toast.error("Couldn't add"); }
    finally { setBusy(false); }
  };

  const saveNote = async (item, changes) => {
    try { await putItem(item, changes); toast.success("Saved"); onDone?.(); }
    catch { toast.error("Save failed"); }
  };

  // ---- Candidate grid: every non-featured entry, painted in chunks ----
  const query = q.trim().toLowerCase();
  const available = library
    .filter((i) => !i.featured)
    .filter((i) => !query || `${i.title} ${i.creator}`.toLowerCase().includes(query));
  const [shown, setShown] = useState(PAGE);
  useEffect(() => { setShown(PAGE); }, [query]);
  const scrollBox = useRef(null);
  const sentinel = useRef(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setShown((n) => n + PAGE); },
      { root: scrollBox.current, rootMargin: "300px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [available.length]);
  const visible = available.slice(0, shown);

  return (
    <div data-testid="admin-picks-manager">
      <p className="text-sm text-vellum-muted max-w-2xl mb-6">
        Curate the &quot;Currently on my table&quot; spotlight carousel on the homepage — up to {MAX_PICKS} entries, shown in this order. Drag the handle to reorder (or use the arrows), tweak the spotlight note or cover, or remove an entry.
      </p>

      <p className="font-mono text-xs uppercase tracking-[0.2em] text-vellum-muted mb-4" data-testid="picks-slot-count">{rows.length} / {MAX_PICKS} slots used</p>

      <div ref={listRef} className="border border-line divide-y divide-line mb-10 select-none">
        {rows.length === 0 && <p className="px-4 py-10 text-center text-vellum-muted text-sm">No spotlight entries yet — add some below.</p>}
        {rows.map((p, i) => (
          <PickRow key={p.id} item={p} index={i} total={rows.length} onMove={move} onRemove={remove} onSave={saveNote} busy={busy} drag={drag} />
        ))}
      </div>

      <div className="border-t border-line pt-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-vellum-muted">Add to spotlight</p>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-vellum-muted" data-testid="picks-available-count">
            {available.length.toLocaleString()} entr{available.length === 1 ? "y" : "ies"} available
          </p>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or author…" data-testid="picks-search" className="w-72 bg-transparent border border-line rounded-sm px-4 py-2.5 text-vellum text-sm focus:border-crimson focus:outline-none placeholder:text-vellum-muted mb-4" />
        <div ref={scrollBox} className="border border-line p-4 max-h-[560px] overflow-y-auto" data-lenis-prevent data-testid="picks-candidate-grid">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {visible.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => add(item)}
                disabled={rows.length >= MAX_PICKS || busy}
                data-testid={`pick-add-${item.id}`}
                title={`${item.title} — add to spotlight`}
                className="group text-left disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <div className="relative aspect-[2/3] overflow-hidden border border-line bg-ink-800">
                  <CoverImage src={item.cover_url} alt={item.title} focal={item.cover_focal} title={item.title} creator={item.creator} year={item.year} />
                  <span className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-ink/70">
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-brass text-brass">
                      <Plus className="w-3 h-3" /> Add
                    </span>
                  </span>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-crimson mt-2">{item.media_type}</p>
                <p className="font-mono text-[11px] text-vellum-muted truncate">{item.creator}</p>
              </button>
            ))}
          </div>
          <div ref={sentinel} aria-hidden="true" className="h-px" data-testid="picks-candidate-sentinel" />
          {visible.length === 0 && <p className="py-6 text-center text-vellum-muted text-sm">No matches.</p>}
          {shown < available.length && (
            <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-muted">Loading more…</p>
          )}
        </div>
      </div>
    </div>
  );
}
